<script setup>

    /* Zuerst werden die Daten geholt, state ist 0 (loading). Falls wir in AlternativeAddressShow keine Daten bekommen,
    emitten wir state = 3, welches dann weder Show noch Edit rendert. Ansonsten ist state = 1 und wir setzen den Haken
    anhand der Daten aus der Axios-GET-Request.
    State-Übersicht:
        0: loading data
        1: show, Daten vorhanden
        2: edit, Daten vorhanden
        3: keins anzeigen, Daten nicht vorhanden
        4: edit, keine Daten vorhanden (erstes Mal welche eintragen) */


    import { ref, watchEffect } from 'vue';
    import axios from 'axios';
    import { getFromAPI, saveFormViaAPI, getFullUrl } from '../../functionCollection';
    import AlternativeAddressShow from './AlternativeAddressShow.vue';
    import AlternativeAddressEdit from './AlternativeAddressEdit.vue';
    import * as bootstrap from "bootstrap";

    const props = defineProps(['fetchUrl', 'updateUrl', 'deleteUrl', 'toggleUrl', 'userNumber', 'addressType']);
    const state = ref(0);
    const checked = ref(false);
    const hidden = ref(false);
    const titleText = ref("verwenden");
    const notAnymore = ref("nicht mehr ");
    const alternativeAddressInfo = ref(null);
    const result = ref(null);
    const saveConfirm = ref(false);
    const saveError = ref(false);
    const saveErrorData = ref(null);
    const changeError = ref(false);
    const catchAllError = ref(false);
    const deleteConfirm = ref(null);

    // get the current alternative address and set the state accordingly. State logic described above.
    watchEffect(async () => {
        alternativeAddressInfo.value = await getFromAPI(getFullUrl(props.fetchUrl, props.userNumber));
        if (alternativeAddressInfo.value === "") {
            state.value = 3;
        } else {
            state.value = 1;
            if (alternativeAddressInfo.value.active == 1) {
                checked.value = true;
            }
        }
    });

    // Triggers the change of the address usage after confirmation in the modal
    function changeAddressUsage() {
        const toggleData = JSON.parse(JSON.stringify( { active: checked.value } ));
        axios.put(getFullUrl(props.toggleUrl, props.userNumber), toggleData)
        .then(function (response) {
            changeError.value = false;
        }).catch(function (error) {
            changeError.value = true;
        })
    }

    // Triggers a hard-delete of all alternative address data!
    function deleteData() {
          axios.delete(getFullUrl(props.deleteUrl, props.userNumber))
            .then(function (response) {
                deleteConfirm.value = true;
            }).catch(function (error) {
                deleteConfirm.value = false;
            });
    }

    // should save new error data.
    async function saveForm(formData) {
        saveErrorData.value = null;
        formData = JSON.parse(JSON.stringify(alternativeAddressInfo.value));
        result.value = await saveFormViaAPI(getFullUrl(props.updateUrl, props.userNumber), formData);
        if (result.value.success === true) {
            hidden.value = false;
            titleText.value = 'ändern';
            state.value = 1;
            saveConfirm.value = true;
            saveError.value = false;
        } else if (result.value.success === false) {
            saveError.value = true;
            saveErrorData.value = result.value.data.response.data;
            saveConfirm.value = false;
        }
    }

    // This handles checkbox-triggered behaviour. On click, change text and overall state accordingly, then call modal.
    // If no address is yet saved, then create the needed object first: clicking on the checkbox is interpreted as the
    // wish to save a new alternative address then.
    function checkboxChanges() {
        if ((state.value === 1) && (checked.value === true)) {
            // Call the modal and ask if the address really should be used.
            notAnymore.value = "";
            showModal();
        } else if ((state.value === 1) && (checked.value === false)) {
            notAnymore.value = "nicht mehr ";
            showModal();
        } else if ((state.value === 3) && (checked.value === true)) {
            // state == 3 means no data exists yet, so we create the needed object to fill in and open the edit field.
            alternativeAddressInfo.value = {
                anrede: "",
                name1: "",
                name2: "",
                strasse: "",
                plz: "",
                ort: "",
                land: ""
            }
            state.value = 4;
        } else if ((state.value === 4) && (checked.value === false)) {
            state.value = 3;
            checked.value = false;
        } else {
            catchAllError.value = ref(true);
        }
    }

    // resets checkbox when cancelling a usage change.
    function setCheckbox() {
        if (alternativeAddressInfo.value.active === 1) {
            checked.value = true;
        } else {
            checked.value = false;
        }
    }

    // calls the confirm modal. Triggered through button click when attempting to change if the address is used or not.
    function showModal() {
        const confirmModal = new bootstrap.Modal(('#confirmUseModal'+props.addressType));
        confirmModal.show();
    }

    // cancel editing, reset to appropriate state (depending on if previous info exists)
    async function cancelEdit() {
        titleText.value = 'verwenden';
        hidden.value = false;
        state.value = 0;
        alternativeAddressInfo.value = await getFromAPI(getFullUrl(props.fetchUrl, props.userNumber));
        if (alternativeAddressInfo.value === "") {
            state.value = 3;
        } else {
            state.value = 1;
            if (alternativeAddressInfo.value.active == 1) {
                checked.value = true;
            }
        }
    }

</script>

<template>
    <div class="well">
        <div class="header">
            <div class="checkbox-container" :class="{hideText: hidden}">
                <input type="checkbox" v-model="checked" @change="checkboxChanges" :disabled="state === 2">
                <div class="check"></div>
            </div>
            <span>Alternative {{ addressType }} {{ titleText }}</span>
        </div>
        <AlternativeAddressShow
            v-if="state == 1"
            :alternativeAddressInfo="alternativeAddressInfo"
            :addressType="props.addressType"
            @switch-to-edit="state = 2, hidden = true, titleText = 'ändern'"
            @delete-data="deleteData"
        />
        <AlternativeAddressEdit
            v-if="state == 2 || state == 4"
            :alternativeAddressInfo="alternativeAddressInfo"
            :saveErrorData="saveErrorData"
            @save-data="saveForm"
            @cancel-edit="cancelEdit"
        />
    </div>

    <div class="alert alert-success" role="alert" v-if="saveConfirm === true">Die neue {{ props.addressType }} wurde erfolgreich gespeichert!</div>
    <div class="alert alert-danger" role="alert" v-if="saveError === true">
        Leider ist ein Fehler beim Speichern der {{ props.addressType }} passiert. Wir bitten um Entschuldigung.
        Bitte probieren Sie es noch einmal, stellen Sie sicher, dass alle Informationen korrekt waren, oder kontaktieren Sie uns.
    </div>
    <div class="alert alert-danger" role="alert" v-if="changeError === true">
        Leider ist ein Fehler beim Änderungswunsch der Verwendung der {{ props.addressType }} passiert. Wir bitten um Entschuldigung.
        Bitte probieren Sie es noch einmal, oder kontaktieren Sie uns.
    </div>
    <div class="alert alert-danger" role="alert" v-if="catchAllError === true">
        Irgendwas ist schief gelaufen. Bitte laden Sie die Seite neu.
    </div>
    <div class="alert alert-danger" role="alert" v-if="deleteConfirm === false">
        Leider ist beim Löschen der Adresse etwas schief gelaufen. Bitte versuchen Sie es erneut, oder kontaktieren Sie uns.
    </div>
    <div class="alert alert-success" role="alert" v-if="deleteConfirm === true">Die Adresse ist erfolgreich gelöscht worden.</div>

    <div class="modal fade" :id="'confirmUseModal' + addressType"  tabindex="-1">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Möchten Sie die alternative {{ addressType }} wirklich {{ notAnymore }}benutzen?</h4>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <p>
                        Bitte beachten Sie, dass Ihre Addressdaten weiterhin gespeichert bleiben. Möchten Sie diese löschen, so benutzen Sie bitte den "Löschen"-Knopf.
                    </p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="setCheckbox">Abbrechen</button>
                    <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="changeAddressUsage">Bestätigen</button>
                </div>
            </div>
        </div>
    </div>

</template>
